import { useEffect, useRef, useState } from "react";
import {
  createPath,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import z from "zod";

import type { CurrentUser, SessionContext } from "@thalamos/haven-common";
import {
  sameSessionContext,
  sessionContextKey,
  sessionContextOrganisationId,
  sessionContextsFromMemberships,
} from "@thalamos/haven-common";

import api from "../api/index.js";
import { createRoute } from "../routing/createRoute.js";
import { captureReturnTo } from "../routing/returnTo.js";
import { identityHasChanged, writeSessionIdentity } from "./sessionIdentity.js";

/**
 * What the gate has concluded, as a schema so the set of answers is data rather
 * than only a type — a consumer switching on it can be checked exhaustively, and
 * a test can enumerate them.
 *
 * Deliberately not branded. A brand stops one kind of value being passed where
 * another is expected, which is worth it for ids because they are all strings;
 * here nothing else has this shape, and branding would mean parsing a literal
 * into existence at every `setStatus`.
 */
export const SessionContextGateStatusSchema = z.enum([
  /** A context is being applied; nothing should render yet. */
  "resolving",
  /** The user must pick a context, so the picker route should render. */
  "choosing",
  /** A context is set and the app may render. */
  "ready",
  /** The user belongs to nothing, so there is no context to act in. */
  "no-memberships",
  /** The server refused the context, and repeating the request will not help. */
  "failed",
]);

export type SessionContextGateStatus = z.infer<
  typeof SessionContextGateStatusSchema
>;

/**
 * Decides which organisation and kind of work the session acts in, and whether the
 * destination the user arrived at is still theirs to see.
 *
 * Three entry paths converge here. An emailed link naming a
 * `session-context-organisation-id` — and a `session-context-kind` where the
 * organisation offers more than one — picks that context outright, because the
 * sender already decided it. A user with exactly one context gets it selected
 * without being asked. Everyone else is sent to the picker, carrying where they
 * were headed.
 */
export const useSessionContextGate = (
  user: CurrentUser | undefined,
  refetchUser: () => Promise<unknown>,
): SessionContextGateStatus => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<SessionContextGateStatus>("resolving");

  const organisationIdFromUrl = searchParams.get(
    "session-context-organisation-id",
  );
  const sessionContextKindFromUrl = searchParams.get("session-context-kind");

  // Captured once: the param is stripped from the URL as soon as it is applied,
  // but whether the user *arrived* with one decides if a context change was
  // intended, and so whether their destination survives it.
  const arrivedWithOrganisationId = useRef(organisationIdFromUrl !== null);

  // Guards against re-issuing a context change while one is in flight, which
  // would loop as the refetch re-runs this effect.
  const applying = useRef(false);

  // A rejected context must not be retried. The axios instance resolves 4xx
  // rather than throwing, so without this a server that refuses a context turns
  // this effect into a request loop rather than an error.
  const refused = useRef(new Set<string>());

  // Asked at most once per load: any choice, even a context elsewhere, answers
  // the link, and the picker returns to it with the link still on the URL.
  const askedAboutLink = useRef(false);

  useEffect(() => {
    if (!user) {
      setStatus("resolving");
      return;
    }

    const currentOrganisationId = sessionContextOrganisationId(
      user.sessionContext,
    );
    const contexts = sessionContextsFromMemberships(user.memberships);

    // Only a link that names exactly one context is followed: an organisation
    // alone is ambiguous where the user does more than one kind of work there.
    const named =
      organisationIdFromUrl === null
        ? []
        : contexts.filter(
            (context) =>
              context.organisationId === organisationIdFromUrl &&
              (sessionContextKindFromUrl === null ||
                context.kind === sessionContextKindFromUrl),
          );
    const nominated = named.length === 1 ? named[0] : undefined;

    const applySessionContext = (context: SessionContext) => {
      const key = sessionContextKey(context);

      if (applying.current || refused.current.has(key)) {
        return;
      }
      applying.current = true;

      void api.user
        .setSessionContext(context)
        .then(async (response) => {
          if (response.status >= 400) {
            refused.current.add(key);
            setStatus("failed");
            return;
          }

          await refetchUser();
        })
        .finally(() => {
          applying.current = false;
        });
    };

    // The link named a context the user holds and is not already acting as.
    if (nominated && !sameSessionContext(nominated, user.sessionContext)) {
      applySessionContext(nominated);
      return;
    }

    // A link naming only an organisation where the user does several kinds of
    // work cannot say which, and carrying on in a context elsewhere would take
    // them to its destination as somebody it was not issued to.
    const linkIsAmbiguous =
      named.length > 1 &&
      !askedAboutLink.current &&
      !named.some((context) =>
        sameSessionContext(context, user.sessionContext),
      );

    if (!user.sessionContext || linkIsAmbiguous) {
      if (linkIsAmbiguous) {
        askedAboutLink.current = true;
      }
      const only = contexts.length === 1 ? contexts[0] : undefined;

      if (only !== undefined) {
        applySessionContext(only);
        return;
      }

      if (contexts.length === 0) {
        setStatus("no-memberships");
        return;
      }

      setStatus("choosing");

      if (!location.pathname.startsWith(createRoute.sessionContextSelector())) {
        navigate(
          createRoute.sessionContextSelector(captureReturnTo(location)),
          {
            replace: true,
          },
        );
      }
      return;
    }

    const identity = {
      userId: user.id,
      sessionContextOrganisationId: currentOrganisationId,
      kind: user.sessionContext.kind,
    };

    // Someone else — or the same person in another kind of work — has arrived at a
    // destination that was not issued to them. Unless they asked for this
    // context explicitly, drop the destination and start at the top.
    if (!arrivedWithOrganisationId.current && identityHasChanged(identity)) {
      writeSessionIdentity(identity);
      setStatus("ready");
      navigate(createRoute.home(), { replace: true });
      return;
    }

    writeSessionIdentity(identity);
    setStatus("ready");

    // The link has done its job; leaving it on the URL would re-apply on every
    // render and make the address bar misleading once the context changes.
    if (organisationIdFromUrl !== null) {
      const remaining = new URLSearchParams(location.search);
      remaining.delete("session-context-organisation-id");
      remaining.delete("session-context-kind");

      // Assembled by the router's own builder rather than by concatenation, so
      // the separator is its problem: an empty query must not leave a trailing
      // `?`, and a present one must get exactly one.
      navigate(
        createPath({
          pathname: location.pathname,
          search: remaining.toString(),
        }),
        { replace: true },
      );
    }
  }, [
    user,
    organisationIdFromUrl,
    sessionContextKindFromUrl,
    location,
    navigate,
    refetchUser,
  ]);

  return status;
};
